import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import TabsComponent from '../components/ui/TabsComponent';
import { 
  ArrowLeft, 
  Users, 
  Building2, 
  Calendar, 
  BarChart3, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Activity,
  Crown,
  UserCheck,
  UserCog,
  User
} from 'lucide-react';

const UserDashboards = () => {
  const { currentUser } = useAuth();
  const { users, departments, projects, tasks, teams } = useData();
  const navigate = useNavigate();
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Get user count by role - moved up to avoid hoisting issues
  const getUserCountByRole = (role) => {
    if (role === 'all') {
      return users.filter(user => user.isActive).length;
    }
    return users.filter(user => user.isActive && user.role === role).length;
  };

  // Update active tab if current tab has no users
  useEffect(() => {
    const currentTabUserCount = getUserCountByRole(activeTab);
    if (currentTabUserCount === 0 && activeTab !== 'all') {
      // If current tab has no users, switch to 'all' tab
      setActiveTab('all');
    }
  }, [users, activeTab]);

  // Check if user can access this page
  if (currentUser?.role !== 'org_admin' && currentUser?.role !== 'ceo' && currentUser?.role !== 'deputy_ceo') {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">אין הרשאה</h2>
          <p className="text-gray-600">רק מנהל ארגון, מנכ"ל או סמנכ"ל יכולים לגשת לדף זה</p>
        </div>
      </div>
    );
  }

  // Filter users based on search, department and role
  const filteredUsers = users.filter(user => {
    // Check if user has required properties before filtering
    if (!user || !user.name || !user.email) {
      return false;
    }
    
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || user.departmentId === selectedDepartment;
    
    // Filter by role based on active tab
    let matchesRole = true;
    if (activeTab === 'deputy_ceo') {
      matchesRole = user.role === 'deputy_ceo';
    } else if (activeTab === 'dept_manager') {
      matchesRole = user.role === 'dept_manager';
    } else if (activeTab === 'team_manager') {
      matchesRole = user.role === 'team_manager';
    } else if (activeTab === 'employee') {
      matchesRole = user.role === 'employee';
    }
    // 'all' tab shows all users
    
    return user.isActive && matchesSearch && matchesDepartment && matchesRole;
  });

  // Get user statistics
  const getUserStats = (userId) => {
    if (!userId) return {
      totalTasks: 0,
      activeTasks: 0,
      completedTasks: 0,
      overdueTasks: 0,
      totalProjects: 0,
      totalEstimate: 0,
      completedEstimate: 0,
      completionRate: 0
    };
    
    const userTasks = tasks.filter(task => 
      task.assigneeId === userId || 
      (task.assigneeIds && task.assigneeIds.includes(userId))
    );
    
    const userProjects = projects.filter(project => 
      project.teamMembers && project.teamMembers.includes(userId)
    );

    const activeTasks = userTasks.filter(task => task.status === 'במסלול');
    const completedTasks = userTasks.filter(task => task.status === 'הושלם');
    const overdueTasks = userTasks.filter(task => {
      if (!task.dueDate) return false;
      return new Date(task.dueDate) < new Date() && task.status !== 'הושלם';
    });

    const totalEstimate = userTasks.reduce((sum, task) => sum + (task.estimate || 0), 0);
    const completedEstimate = completedTasks.reduce((sum, task) => sum + (task.estimate || 0), 0);

    return {
      totalTasks: userTasks.length,
      activeTasks: activeTasks.length,
      completedTasks: completedTasks.length,
      overdueTasks: overdueTasks.length,
      totalProjects: userProjects.length,
      totalEstimate,
      completedEstimate,
      completionRate: userTasks.length > 0 ? Math.round((completedTasks.length / userTasks.length) * 100) : 0
    };
  };

  // Get department name
  const getDepartmentName = (departmentId) => {
    if (!departmentId || !departments) return 'ללא מחלקה';
    const dept = departments.find(d => d.id === departmentId);
    return dept ? dept.name : 'לא ידוע';
  };

  // Get team names - support multiple teams
  const getTeamNames = (userId) => {
    if (!userId || !teams) return [];
    
    const user = users.find(u => u.id === userId);
    if (!user) return [];
    
    const userTeams = [];
    
    // Method 1: Check new multi-team structure
    if (user.teamIds && Array.isArray(user.teamIds)) {
      user.teamIds.forEach(teamId => {
        const team = teams.find(t => t.id === teamId);
        if (team) userTeams.push(team.name);
      });
    }
    
    // Method 2: Check if user has old single teamId field (backward compatibility)
    if (user.teamId && userTeams.length === 0) {
      const team = teams.find(t => t.id === user.teamId);
      if (team) userTeams.push(team.name);
    }
    
    // Method 3: Check if user is in teamMembers array (fallback)
    if (userTeams.length === 0) {
      teams.forEach(t => {
        if (t.teamMembers && Array.isArray(t.teamMembers) && t.teamMembers.includes(userId)) {
          userTeams.push(t.name);
        }
      });
    }
    
    return userTeams;
  };



  // Check if data is loaded
  if (!users || !departments || !projects || !tasks) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Activity className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">טוען נתונים...</h2>
          <p className="text-gray-600">אנא המתן עד שהנתונים יטענו</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">דשבורדים של עובדים</h1>
            <p className="text-gray-600">צפייה בדשבורדים של כל העובדים במערכת</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="חיפוש עובדים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="min-w-48">
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">כל המחלקות</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Role Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <TabsComponent 
          tabs={[
            {
              id: 'all',
              label: 'כל העובדים',
              icon: Users,
              badge: getUserCountByRole('all').toString(),
              content: null
            },
            {
              id: 'deputy_ceo',
              label: 'סמנכ"לים',
              icon: Crown,
              badge: getUserCountByRole('deputy_ceo').toString(),
              content: null
            },
            {
              id: 'dept_manager',
              label: 'מנהלי מחלקה',
              icon: UserCog,
              badge: getUserCountByRole('dept_manager').toString(),
              content: null
            },
            {
              id: 'team_manager',
              label: 'מנהלי צוות',
              icon: UserCheck,
              badge: getUserCountByRole('team_manager').toString(),
              content: null
            },
            {
              id: 'employee',
              label: 'עובדים',
              icon: User,
              badge: getUserCountByRole('employee').toString(),
              content: null
            }
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Crown className="w-6 h-6 text-blue-600" />
            </div>
            <div className="mr-3">
              <p className="text-sm text-gray-600">סמנכ"לים</p>
              <p className="text-2xl font-bold text-blue-600">{getUserCountByRole('deputy_ceo')}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCog className="w-6 h-6 text-green-600" />
            </div>
            <div className="mr-3">
              <p className="text-sm text-gray-600">מנהלי מחלקה</p>
              <p className="text-2xl font-bold text-green-600">{getUserCountByRole('dept_manager')}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserCheck className="w-6 h-6 text-purple-600" />
            </div>
            <div className="mr-3">
              <p className="text-sm text-gray-600">מנהלי צוות</p>
              <p className="text-2xl font-bold text-purple-600">{getUserCountByRole('team_manager')}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <User className="w-6 h-6 text-orange-600" />
            </div>
            <div className="mr-3">
              <p className="text-sm text-gray-600">עובדים</p>
              <p className="text-2xl font-bold text-orange-600">{getUserCountByRole('employee')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map(user => {
          const stats = getUserStats(user.id);
          
          return (
            <div key={user.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
              {/* User Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-lg">{user.name}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.role === 'org_admin' || user.role === 'ceo' || user.role === 'deputy_ceo' ? 'bg-red-100 text-red-800' :
                    user.role === 'dept_manager' || user.role === 'team_manager' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {(() => {
                      switch (user.role) {
                        case 'org_admin':
                          return 'מנהל ראשי';
                        case 'ceo':
                          return 'מנכ"ל';
                        case 'deputy_ceo':
                          return 'סמנכ"ל';
                        case 'dept_manager':
                          return 'מנהל מחלקה';
                        case 'team_manager':
                          return 'מנהל צוות';
                        case 'employee':
                          return 'עובד';
                        default:
                          return 'עובד';
                      }
                    })()}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{user.email}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {getDepartmentName(user.departmentId)}
                </p>
                {getTeamNames(user.id).length > 0 && (
                  <p className="text-xs text-purple-500 mt-1">
                    צוותים: {getTeamNames(user.id).join(', ')}
                  </p>
                )}
              </div>

              {/* Stats Grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.totalTasks}</div>
                    <div className="text-xs text-gray-500">סה"כ משימות</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.completedTasks}</div>
                    <div className="text-xs text-gray-500">הושלמו</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.activeTasks}</div>
                    <div className="text-xs text-gray-500">פעילות</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{stats.overdueTasks}</div>
                    <div className="text-xs text-gray-500">באיחור</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>אחוז השלמה</span>
                    <span>{stats.completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${stats.completionRate}%` }}
                    ></div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">פרויקטים:</span>
                    <span className="font-medium">{stats.totalProjects}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">שעות מוערכות:</span>
                    <span className="font-medium">{stats.totalEstimate}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">שעות שהושלמו:</span>
                    <span className="font-medium">{stats.completedEstimate}h</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => setSelectedUser(user)}
                    className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    צפה בדשבורד מפורט
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* No Users Message */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeTab === 'all' ? 'לא נמצאו עובדים' : `לא נמצאו ${(() => {
              switch (activeTab) {
                case 'deputy_ceo': return 'סמנכ"לים';
                case 'dept_manager': return 'מנהלי מחלקה';
                case 'team_manager': return 'מנהלי צוות';
                case 'employee': return 'עובדים';
                default: return 'עובדים';
              }
            })()}`}
          </h3>
          <p className="text-gray-600">
            {activeTab === 'all' 
              ? 'נסה לשנות את החיפוש או הסינון' 
              : 'נסה לשנות את הטאב או הסינון'
            }
          </p>
        </div>
      )}

      {/* Detailed User Dashboard Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                דשבורד מפורט - {selectedUser.name}
              </h3>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">תפקיד</div>
                    <div className="font-medium">{(() => {
                      switch (selectedUser.role) {
                        case 'org_admin':
                          return 'מנהל ראשי';
                        case 'ceo':
                          return 'מנכ"ל';
                        case 'deputy_ceo':
                          return 'סמנכ"ל';
                        case 'dept_manager':
                          return 'מנהל מחלקה';
                        case 'team_manager':
                          return 'מנהל צוות';
                        case 'employee':
                          return 'עובד';
                        default:
                          return 'עובד';
                      }
                    })()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">מחלקה</div>
                    <div className="font-medium">{getDepartmentName(selectedUser.departmentId)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">צוות</div>
                    <div className="font-medium">{
                      getTeamNames(selectedUser.id).length > 0 
                        ? getTeamNames(selectedUser.id).join(', ')
                        : 'ללא צוות'
                    }</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">קיבולת שבועית</div>
                    <div className="font-medium">{selectedUser.weeklyCapacity || 40}h</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">סטטוס</div>
                    <div className="font-medium">{selectedUser.isActive ? 'פעיל' : 'לא פעיל'}</div>
                  </div>
                </div>
              </div>

              {/* Tasks Overview */}
              <div>
                <h4 className="text-lg font-medium mb-3">משימות</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tasks
                    .filter(task => 
                      task.assigneeId === selectedUser.id || 
                      (task.assigneeIds && task.assigneeIds.includes(selectedUser.id))
                    )
                    .slice(0, 6)
                    .map(task => (
                      <div key={task.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-sm">{task.title}</h5>
                          <span className={`px-2 py-1 rounded text-xs ${
                            task.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                            task.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {task.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">{task.description}</p>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>הערכה: {task.estimate || 0}h</span>
                          {task.dueDate && (
                            <span>דדליין: {new Date(task.dueDate).toLocaleDateString('he-IL')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Projects Overview */}
              <div>
                <h4 className="text-lg font-medium mb-3">פרויקטים</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {projects
                    .filter(project => 
                      project.teamMembers && project.teamMembers.includes(selectedUser.id)
                    )
                    .slice(0, 4)
                    .map(project => (
                      <div key={project.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium">{project.name}</h5>
                          <span className={`px-2 py-1 rounded text-xs ${
                            project.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                            project.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {project.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>התקדמות: {project.progress || 0}%</span>
                          {project.startDate && (
                            <span>התחלה: {new Date(project.startDate).toLocaleDateString('he-IL')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboards;
